import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { downloadLegalInvoicePdf } from '@/pages/billing/hooks/useLegalInvoices';
import { useLicenseOrderAction } from './hooks/useLicenseOrders';
import type { LicenseOrderDetail } from './types';

interface Props {
  orderId: string;
  detail: LicenseOrderDetail;
}

type CorrectionResult = {
  already?: boolean;
  originalInvoiceNumber?: string;
  creditNoteId?: string;
  creditNoteNumber?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  totalCents?: number;
};

function centsToInput(cents: number | null | undefined): string {
  const [whole, frac] = ((cents || 0) / 100).toFixed(2).split('.');
  return `${whole},${frac}`;
}

function parseEuro(raw: string): number | null {
  const text = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(text)) return null;
  const [whole, frac = ''] = text.split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents) || cents > 200_000) return null;
  return cents;
}

function fmtEur(cents: number): string {
  return (
    (cents / 100).toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + '\u00a0€'
  );
}

export function LicenseInvoiceCorrectionPanel({ orderId, detail }: Props) {
  const action = useLicenseOrderAction(orderId);
  const [open, setOpen] = useState(false);
  const [officialFee, setOfficialFee] = useState(() => centsToInput(detail.officialFeeCents));
  const [serviceFee, setServiceFee] = useState(() => centsToInput(detail.serviceFeeCents));
  const [error, setError] = useState('');
  const [result, setResult] = useState<CorrectionResult | null>(null);

  useEffect(() => {
    setOfficialFee(centsToInput(detail.officialFeeCents));
    setServiceFee(centsToInput(detail.serviceFeeCents));
    setResult(null);
    setError('');
  }, [detail.legalInvoiceId, detail.officialFeeCents, detail.serviceFeeCents]);

  if (!detail.paidAt || !detail.legalInvoiceNumber || detail.status === 'refunded') return null;

  const officialCents = parseEuro(officialFee);
  const serviceCents = parseEuro(serviceFee);
  const same =
    officialCents === detail.officialFeeCents && serviceCents === detail.serviceFeeCents;
  const totalCents =
    officialCents != null && serviceCents != null ? officialCents + serviceCents : null;

  async function onCorrect() {
    if (officialCents == null || serviceCents == null || totalCents == null || same) return;
    const accepted = window.confirm(
      `Se rectifica ${detail.legalInvoiceNumber} por su total y se emite una factura nueva de ${fmtEur(totalCents)}. No se devuelve dinero.`
    );
    if (!accepted) return;
    setError('');
    try {
      const data = (await action.mutateAsync({
        action: 'correct_invoice',
        officialFee,
        serviceFee,
      })) as CorrectionResult | undefined;
      setResult(data ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido corregir la factura');
    }
  }

  async function download(id: string | undefined, number: string | undefined) {
    if (!id || !number) return;
    try {
      await downloadLegalInvoicePdf(id, number);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al descargar el PDF');
    }
  }

  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="text-sm font-medium text-primary"
      >
        {open ? 'Cerrar corrección de factura' : 'Corregir factura'}
      </button>
      {open && (
        <div className="space-y-2">
          <p className="text-[11px] text-muted-foreground">
            Anula {detail.legalInvoiceNumber} con una rectificativa por su importe y emite otra
            con la tasa y la gestión de abajo. El cobro no se toca.
          </p>
          <label className="block">
            <span className="text-[11px] text-muted-foreground">Tasa oficial (€)</span>
            <input
              value={officialFee}
              onChange={(event) => {
                setOfficialFee(event.target.value);
                setResult(null);
              }}
              inputMode="decimal"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[11px] text-muted-foreground">Gestión AiPeces (€)</span>
            <input
              value={serviceFee}
              onChange={(event) => {
                setServiceFee(event.target.value);
                setResult(null);
              }}
              inputMode="decimal"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <p className="text-xs text-muted-foreground">
            Factura nueva:{' '}
            {totalCents != null && totalCents > 0 ? fmtEur(totalCents) : 'importe no válido'}
          </p>
          <button
            type="button"
            disabled={action.isPending || same || totalCents == null || totalCents <= 0}
            onClick={() => void onCorrect()}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary text-sm disabled:opacity-50"
          >
            {action.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            Rectificar y emitir factura nueva
          </button>
          {error ? <p className="text-xs text-rose-500">{error}</p> : null}
          {result?.invoiceNumber ? (
            <div className="space-y-1 text-xs text-emerald-600">
              <p>
                {result.already
                  ? `Ya estaba corregida. Factura vigente ${result.invoiceNumber}.`
                  : `Rectificativa ${result.creditNoteNumber}. Factura nueva ${result.invoiceNumber}.`}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="underline"
                  onClick={() => void download(result.creditNoteId, result.creditNoteNumber)}
                >
                  Descargar rectificativa
                </button>
                <button
                  type="button"
                  className="underline"
                  onClick={() => void download(result.invoiceId, result.invoiceNumber)}
                >
                  Descargar factura nueva
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
